import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { teacherApi } from "@/lib/api";
import {
  Shield, Wrench, Database, Gift, Sparkles, History, Mail, FileSearch,
  ArrowLeft, TrendingUp, MessageSquareWarning, Users, AlertTriangle, Rocket, FlaskConical, Inbox,
} from "lucide-react";

import { DOT_COLOR } from "./admin/_shared";
import MaintenanceTab from "./admin/MaintenanceTab";
import BackupsTab from "./admin/BackupsTab";
import TrialTab from "./admin/TrialTab";
import RejudgeTab from "./admin/RejudgeTab";
import AuditTab from "./admin/AuditTab";
import EmailQueueTab from "./admin/EmailQueueTab";
import EmailLogTab from "./admin/EmailLogTab";
import FeedbackTab from "./admin/FeedbackTab";
import UserStatsTab from "./admin/UserStatsTab";
import InvitationsTab from "./admin/InvitationsTab";
import UsersTab from "./admin/UsersTab";
import IncidentsTab from "./admin/IncidentsTab";
import OnboardingChecklistTab from "./admin/OnboardingChecklistTab";
import CurationTab from "./admin/CurationTab";
import ProposalsTab from "./admin/ProposalsTab";
import DeliverabilityTab from "./admin/DeliverabilityTab";

export default function AdminPanel() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("onboarding");
  const [health, setHealth] = useState(null);

  // Lightweight polling — every 60s so the sidebar dots stay fresh without
  // bothering Mongo. Initial fetch happens on mount.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await teacherApi.get("/admin/health-summary");
        if (!cancelled) setHealth(data);
      } catch {
        /* silent — dots just stay neutral if endpoint fails */
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Map each tab id to the health bucket that influences its dot
  const tabHealth = (tabId) => {
    if (!health) return null;
    if (tabId === "maintenance") return health.maintenance?.status;
    if (tabId === "backups") return health.backups?.status;
    if (tabId === "email-queue") return health.email_queue?.status;
    if (tabId === "feedback") return health.feedback?.status;
    if (tabId === "users") return health.users?.status;
    if (tabId === "user-stats") return health.user_stats?.status;
    if (tabId === "incidents") return health.incidents?.status;
    if (tabId === "deliverability") return health.deliverability?.status;
    return null;
  };

  // When admin opens one of the "activity dots" tabs, mark it seen so the dot
  // clears on next poll. Non-blocking — if the POST fails the dot will just
  // stay until the next activity-free poll.
  const _ACTIVITY_TABS = new Set(["feedback", "users", "user-stats", "incidents"]);
  const openTab = (tabId) => {
    setTab(tabId);
    if (_ACTIVITY_TABS.has(tabId)) {
      teacherApi.post("/admin/tab-seen", { tab_id: tabId }).catch(() => {/* silent */});
    }
  };

  const TABS = [
    { id: "onboarding", label: "🚀 Onboarding", icon: Rocket, Comp: OnboardingChecklistTab },
    { id: "maintenance", label: t("admin.tabs.maintenance"), icon: Wrench, Comp: MaintenanceTab },
    { id: "backups", label: t("admin.tabs.backups"), icon: Database, Comp: BackupsTab },
    { id: "trial", label: t("admin.tabs.trial"), icon: Gift, Comp: TrialTab },
    { id: "rejudge", label: t("admin.tabs.rejudge"), icon: Sparkles, Comp: RejudgeTab },
    { id: "curation", label: "🧪 Audyt bazy", icon: FlaskConical, Comp: CurationTab },
    { id: "proposals", label: "📥 Propozycje", icon: Inbox, Comp: ProposalsTab },
    { id: "audit", label: t("admin.tabs.audit"), icon: History, Comp: AuditTab },
    { id: "email-queue", label: t("admin.tabs.email_queue"), icon: Mail, Comp: EmailQueueTab },
    { id: "email-log", label: t("admin.tabs.email_log"), icon: FileSearch, Comp: EmailLogTab },
    { id: "deliverability", label: "📈 Deliverability", icon: TrendingUp, Comp: DeliverabilityTab },
    { id: "feedback", label: t("admin.tabs.feedback"), icon: MessageSquareWarning, Comp: FeedbackTab },
    { id: "user-stats", label: "Statystyki", icon: TrendingUp, Comp: UserStatsTab },
    { id: "users", label: "Użytkownicy", icon: Users, Comp: UsersTab },
    { id: "invitations", label: "Zaproszenia", icon: Gift, Comp: InvitationsTab },
    { id: "incidents", label: "Incydenty", icon: AlertTriangle, Comp: IncidentsTab },
  ];

  const ActiveTab = TABS.find((x) => x.id === tab)?.Comp;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="brut border-b-2 border-[#0F172A] bg-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Shield className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="text-3xl font-black tracking-tight">{t("admin.panel_title")}</h1>
            {health?.overall && (
              <span
                className={`inline-block w-3 h-3 rounded-full border-2 border-[#0F172A]${health.overall !== "green" ? " dot-pulse" : ""}`}
                style={{ background: DOT_COLOR[health.overall], color: DOT_COLOR[health.overall] }}
                title={`${t("admin.overall_health")}: ${health.overall}`}
                data-testid="admin-overall-health-dot"
              />
            )}
          </div>
          <Link to="/teacher" className="text-sm font-bold underline" data-testid="admin-back-link">
            <ArrowLeft className="inline w-4 h-4 mr-1" strokeWidth={2.5} />
            {t("admin.back_to_dashboard")}
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex gap-2 flex-wrap" data-testid="admin-tabs">
          {TABS.map((t2) => {
            const I = t2.icon;
            const dot = tabHealth(t2.id);
            return (
              <button
                key={t2.id}
                onClick={() => openTab(t2.id)}
                className={`brut brut-hover px-4 py-2 rounded-md border-2 border-[#0F172A] font-bold text-sm flex items-center gap-2 ${tab === t2.id ? "bg-[#FEF08A]" : "bg-white"}`}
                data-testid={`admin-tab-${t2.id}`}
              >
                <I className="w-4 h-4" strokeWidth={2.5} />
                <span>{t2.label}</span>
                {dot && dot !== "green" && (
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full border border-[#0F172A] dot-pulse"
                    style={{ background: DOT_COLOR[dot], color: DOT_COLOR[dot] }}
                    data-testid={`admin-tab-${t2.id}-dot`}
                    title={dot === "red" ? t("admin.requires_attention") : t("admin.warning")}
                  />
                )}
              </button>
            );
          })}
        </div>

        {ActiveTab && <ActiveTab />}
      </div>
    </div>
  );
}
